import { create } from 'zustand';
import type { StoreApi, UseBoundStore } from 'zustand';

import type { QueryParams } from '../../api/core/params';
import type { Repo } from '../../api/core/repository';
import { userErrorMessage } from '../userError';

// Zustand take on lms-fe's initStore: one store shape per Repository, no per-domain boilerplate.
// Domain stores compose this and add their own actions.

export interface ResourceState<T> {
  items: T[];
  item: T | null;
  total: number;
  loading: boolean;
  error: string | null;
  fetch: (params?: QueryParams) => Promise<T[]>;
  fetchOne: (id: string | number, params?: QueryParams) => Promise<T | null>;
  count: (params?: QueryParams) => Promise<number>;
  create: (data: unknown) => Promise<T | null>;
  update: (id: string | number, data: unknown) => Promise<T | null>;
  remove: (id: string | number) => Promise<boolean>;
  reset: () => void;
}

export type ResourceStore<T> = UseBoundStore<StoreApi<ResourceState<T>>>;

export function createResourceStore<T>(repo: Repo<T>): ResourceStore<T> {
  return create<ResourceState<T>>((set) => {
    // Every action funnels through here so loading and error handling stay in one place.
    async function run<R>(work: () => Promise<R>, fallback: R): Promise<R> {
      set({ loading: true, error: null });
      try {
        const result = await work();
        set({ loading: false });
        return result;
      } catch (error) {
        set({ loading: false, error: userErrorMessage(error) });
        return fallback;
      }
    }

    return {
      items: [],
      item: null,
      total: 0,
      loading: false,
      error: null,

      fetch: (params?: QueryParams) =>
        run<T[]>(async () => {
          const items = await repo.fetch<T[]>(params);
          const safe = Array.isArray(items) ? items : [];
          set({ items: safe });
          return safe;
        }, []),

      fetchOne: (id: string | number, params?: QueryParams) =>
        run<T | null>(async () => {
          const item = await repo.fetchOne<T>(id, params);
          set({ item: item || null });
          return item || null;
        }, null),

      count: (params?: QueryParams) =>
        run<number>(async () => {
          const total = await repo.count(params);
          set({ total });
          return total;
        }, 0),

      create: (data: unknown) => run<T | null>(async () => (await repo.create<T>(data)) || null, null),

      update: (id: string | number, data: unknown) =>
        run<T | null>(async () => (await repo.update<T>(id, data)) || null, null),

      remove: (id: string | number) =>
        run<boolean>(async () => {
          await repo.remove(id);
          set((state) => ({
            items: state.items.filter(
              (entry) => String((entry as unknown as { id?: unknown }).id) !== String(id)
            ),
          }));
          return true;
        }, false),

      reset: () => set({ items: [], item: null, total: 0, loading: false, error: null }),
    };
  });
}
